import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/forms/OtpInput';
import { requestLoginOtp, verifyLoginOtp } from '@/api/auth';
import { WEB_ROLES } from '@/api/scope';
import type { Profile } from '@/api/types/common';
import { useAuth } from '@/store/auth';
import { NightBackground } from './NightBackground';

const OTP_TTL_SECONDS = 5 * 60;
const OTP_LENGTH = 6;

export default function OtpVerifyPage() {
  const navigate = useNavigate();
  const pendingPhone = useAuth((s) => s.pendingPhone);
  const setToken = useAuth((s) => s.setToken);
  const setProfile = useAuth((s) => s.setProfile);
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(OTP_TTL_SECONDS);
  // Track which otp value we already attempted, so a failed auto-verify
  // doesn't loop on the same digits.
  const lastAttemptedRef = useRef<string>('');

  useEffect(() => {
    if (!pendingPhone) {
      navigate('/login', { replace: true });
      return;
    }
    const id = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [pendingPhone, navigate]);

  const verify = useCallback(
    async (code: string) => {
      if (!pendingPhone) return;
      if (code.length < OTP_LENGTH) return;
      setSubmitting(true);
      try {
        const { token, user, profiles } = await verifyLoginOtp({ ...pendingPhone, password: code });
        // `token` is undefined on cookie-auth (web); session is established
        // server-side via HttpOnly cookie. Storing null is intentional.
        setToken(token ?? null, {
          id: user.id,
          mobile_no: user.mobile_no ?? '',
          country_code: user.country_code ?? '',
          name: user.name,
        });

        if (profiles.length === 0) {
          toast.error('No profile found for this account');
          return;
        }

        // The web console renders admin + manager. Trainer/student profiles
        // exist on the same login but have no web UI, so only web roles are
        // eligible to become the active profile.
        const webProfiles = profiles.filter((p) => WEB_ROLES.has(p.role));

        if (webProfiles.length === 0) {
          navigate('/unsupported-role', { replace: true });
          return;
        }
        if (webProfiles.length === 1) {
          setProfile(webProfiles[0]);
          navigate(routeForProfile(webProfiles[0]), { replace: true });
          return;
        }
        // Multiple web-supported profiles (e.g. admin + manager) — let the user pick.
        navigate('/login/profile', { replace: true });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Invalid OTP');
      } finally {
        setSubmitting(false);
      }
    },
    [pendingPhone, setToken, setProfile, navigate],
  );

  // Auto-verify on last-digit entry. Re-runs whenever the user edits and
  // arrives at a new full code; the ref guard prevents looping on a rejected one.
  useEffect(() => {
    if (otp.length !== OTP_LENGTH) return;
    if (submitting) return;
    if (lastAttemptedRef.current === otp) return;
    lastAttemptedRef.current = otp;
    void verify(otp);
  }, [otp, submitting, verify]);

  if (!pendingPhone) return null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (otp.length === 0) {
      toast.error('OTP is required');
      return;
    }
    if (otp.length < OTP_LENGTH) {
      toast.error('Please enter the full OTP');
      return;
    }
    lastAttemptedRef.current = otp;
    void verify(otp);
  }

  async function onResend() {
    if (!pendingPhone) return;
    setResending(true);
    try {
      await requestLoginOtp(pendingPhone);
      setSecondsLeft(OTP_TTL_SECONDS);
      lastAttemptedRef.current = '';
      toast.success('OTP resent');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <NightBackground />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 lg:justify-end lg:pr-16">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center lg:text-left">
            <img
              src="/icons/partapp-icon.png"
              alt="PartApp"
              width={56}
              height={56}
              className="mx-auto mb-4 rounded-xl shadow-lg lg:mx-0"
            />
            <h1 className="text-3xl font-bold text-white">Verify your number</h1>
            <p className="mt-2 text-sm text-white/80">
              We've sent a code to +{pendingPhone.country_code} {pendingPhone.mobile_no}.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-6 rounded-2xl border border-white/10 bg-white/95 p-6 shadow-2xl backdrop-blur-md dark:bg-zinc-900/95"
          >
            <div className="flex justify-center">
              <OtpInput value={otp} onChange={setOtp} length={OTP_LENGTH} />
            </div>

            <Button type="submit" loading={submitting} fullWidth size="lg">
              Verify
            </Button>

            <div className="flex items-center justify-between text-xs text-app-hint">
              <span>{secondsLeft > 0 ? `Code expires in ${formatTime(secondsLeft)}` : 'Code expired'}</span>
              <button
                type="button"
                onClick={onResend}
                disabled={resending || secondsLeft > OTP_TTL_SECONDS - 30}
                className="font-semibold text-primary disabled:text-app-hint"
              >
                {resending ? 'Resending…' : 'Resend OTP'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function routeForProfile(p: Profile): string {
  switch (p.role) {
    case 'admin':
    case 'manager':
    case 'trainer':
      // Manager/trainer data is scoped server-side; all web roles share the /admin shell.
      return '/admin';
    default:
      return '/unsupported-role';
  }
}
